import { ExtendedSelection, Layout, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { htmlToText } from 'html-to-text';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { Prices } from '~/components/prices';
import { CardContent } from '~/components/selection-card-new/card-content';
import { SelectionCardProps } from '~/components/selection-card-new/models/selection-card-props';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const hasTeaserText = (teaserText: string | null | undefined) => teaserText && teaserText.trim();

export const SelectionCard = ({
	item: {
		selected,
		readonly,
		loading,
		onSelect,
		name,
		imgUrl,
		price,
		textLink,
		standard,
		onDescriptionToggle,
		disclaimerContent,
		disclaimerHeadline,
		disclaimerStartDate,
		disclaimerEndDate,
		key,
	},
	withImage = false,
	type,
	children,
	id,
}: SelectionCardProps) => {
	const disabled = useMemo(() => readonly || loading, [loading, readonly]);
	const { i18n } = useI18n();

	const onClick = useCallback(() => {
		if (readonly) return;
		onSelect();
	}, [readonly, onSelect]);

	const onTextLinkClick = useCallback(
		(event: MouseEvent) => {
			event.stopPropagation();
			event.preventDefault();
			if (!textLink) return;
			textLink.onTextLinkClick();
		},
		[textLink],
	);

	const priceComponent = useMemo(() => {
		if (!price) return undefined;

		return standard ? (
			<Text tag="p" variant={{ type: 'copy', order: '3', style: 'normal' }}>
				{i18n('ngc.common.standard')}
			</Text>
		) : (
			<Prices
				id={`${id}-price-information`}
				totalPrice={price.totalPrice}
				pr7={key}
				vatConfig={price.feature}
				size="3"
			/>
		);
	}, [i18n, id, key, price, standard]);

	const content = (
		<Layout direction="column" rowGap="16px">
			{priceComponent}
			<CardContent
				disclaimerHeadline={disclaimerHeadline}
				disclaimerContent={disclaimerContent}
				disclaimerStartDate={disclaimerStartDate}
				disclaimerEndDate={disclaimerEndDate}
				alwaysVisible={selected}
				onDescriptionToggle={onDescriptionToggle}
				linksSection={
					textLink ? (
						<TextLink size="sm" variant="standalone" onClick={onTextLinkClick}>
							{textLink.label}
						</TextLink>
					) : null
				}>
				{children}
			</CardContent>
		</Layout>
	);

	return (
		<ExtendedSelection
			content={content}
			disabled={disabled}
			id={`${id}-selection-card`}
			imgLG={
				withImage && imgUrl
					? {
							alt: name || '',
							src: imgUrl || '',
							ratio: '16/9',
						}
					: undefined
			}
			label={htmlToText(name || '')}
			onClick={onClick}
			type={type}
			value=""
			name={''}
			checked={selected}
			data-testid={DATA_TEST_ID.SELECTION_CARD_CONTAINER}
		/>
	);
};
